/*
 * Levels Beyond CONFIDENTIAL
 *
 * Copyright 2003 - 2020 Levels Beyond Incorporated
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains the
 * property of Levels Beyond Incorporated and its suppliers, if
 * any. The intellectual and technical concepts contained herein
 * are proprietary to Levels Beyond Incorporated and its suppliers
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this
 * material is unlawful and strictly forbidden unless prior
 * written permission is obtained from Levels Beyond Incorporated.
 */

/**
 * Created by seaster on 2/16/16.
 */
'use strict';
angular.module('prism-ui.partner').controller('PartnerPropertiesViewController', function (
    _,
    $location,
    $q,
    $routeParams,
    $scope,
    $timeout,
    uiGridConstants,
    AcceptProfileResourceFactory,
    DebugService,
    DynamicPropertiesService,
    IntermediateTemplatesResourceFactory,
    LanguagesService,
    PackageResourceFactory,
    PartnerEditService,
    PartnerResourceFactory,
    RoleResourceFactory,
    TerritoriesService)
{
    let self = this;
    self.DEFAULT_FORMAT_VALUES = {
        dateFormat: 'MM/dd/yyyy',
        dateTimeFormat: 'MM/dd/yyyy hh:mm:ss a zz',
        timeFormat: 'hh:mm:ss a zz',
        numberFormat: '#.####',
        timeZone: 'PST'
    };

    //=================================//
    // Default Artwork Accept Profiles //
    //=================================//

    self.onArtworkAcceptProfilesChanged = (artworkAcceptProfiles) => {
        self.partnerObject.artwork = artworkAcceptProfiles;
        self.editorForm.$setDirty();
    };

    // Called when partner's artwork array should be
    // rebuilt. (specifically: During initialization and when
    // reverting)
    const resetRequiredArtworksGrid = () => {
        // Gives the grid a copy of the data, so it doesn't revert to partner's artwork after every change
        let bothCategory = _.find(self.availableCategoryTypes, {name: 'Both'});
        self.initialArtworkAcceptProfiles = _.clone(self.partnerObject.artwork);
        _.forEach(self.initialArtworkAcceptProfiles, (artwork) => {
            if (!artwork.packageCategoryType) {
                artwork.packageCategoryType = bothCategory;
            }
        });
    };

    //=======================//
    // Serviceable Languages //
    //=======================//

    const serviceableLanguageFlags = [
        'cCEnabled', 'subtitleEnabled', 'audioEnabled', 'artworkEnabled'
    ];

    //
    // Creates a new ServiceableLanguage object for the given
    // language.
    //
    const createServiceableLanguage = (language) => {
        return {
            language,
            cCEnabled: false,
            subtitleEnabled: false,
            audioEnabled: false,
            artworkEnabled: false,
        };
    };

    const serviceableLanguageUsed = (serviceableLanguage) => {
        return _.some(serviceableLanguageFlags, (flagName) => {
            return (serviceableLanguage[flagName] === true);
        });
    };

    const getServiceableLanguageIndex = (serviceableLanguageList, language) => {
        if (language) {
            return _.findIndex(serviceableLanguageList, {
                language: {id: language.id}
            });
        }
        return _.findIndex(serviceableLanguageList, (serviceableLanguage) => {
            return (!serviceableLanguage.language);
        });
    };

    //
    // Called when the serviceable languages array should be
    // rebuilt (specifically, during initialization and when
    // reverting).
    //
    const updateServiceableLanguages = () => {
        _.forEach(self.allAvailableLanguages.concat(undefined), (language) => {
            let partnerIndex = getServiceableLanguageIndex(self.partnerObject.serviceableLanguages, language);
            let serviceableLanguage;
            if (partnerIndex >= 0) {
                serviceableLanguage = self.partnerObject.serviceableLanguages[partnerIndex];
                // If language isn't used, don't keep this language
                if (!serviceableLanguageUsed(serviceableLanguage)) {
                    self.partnerObject.serviceableLanguages.splice(partnerIndex, 1);
                }
            } else {
                serviceableLanguage = createServiceableLanguage(language);
            }

            // Add language to visible list
            let gridIndex = getServiceableLanguageIndex(self.availableServiceableLanguages, language);
            if (gridIndex >= 0) {
                _.assign(self.availableServiceableLanguages[gridIndex], serviceableLanguage);
            } else {
                self.availableServiceableLanguages.push(serviceableLanguage);
            }
        });

        self.availableServiceableLanguages = _
            .chain(self.availableServiceableLanguages)
            .orderBy('language.name')
            .partition({language: {name: 'English'}})
            .flatten()
            .partition((serviceableLanguage) => !serviceableLanguage.language)
            .flatten()
            .value();

        // If nothing selected, implies serviceableLanguage grid "All" chosen (and in valid state),
        // so make array valid also
        if (!self.partnerObject.serviceableLanguages) {
            self.partnerObject.serviceableLanguages = [];
        }
        $timeout(self.editorForm.$setPristine);
    };

    self.onServiceableLanguagesChanged = (validServiceableLanguages) => {
        self.partnerObject.serviceableLanguages = validServiceableLanguages;
        self.editorForm.$setDirty();
    };

    //====================//
    // Delivery Templates //
    //====================//

    self.deliveryTemplateGridColumns = [
        {
            name: 'checkMarkRowHeaderCol',
            field: 'checkMark',
            width: 30,
            enableColumnMenu: false,
            enableSorting: false,
            enableColumnResizing: false,
            cellTemplate: 'components/visual/grid/check-mark-template.html',
            headerCellTemplate: '<div class="ui-grid-cell-contents" col-index="renderIndex" ng-click="grid.appScope.partnerPropCtrlr.toggleRows($event)">' +
            '<input type="checkbox" ng-checked="grid.selection.selectAll"/><label></label></div>'
        },
        {
            field: 'name',
            name: 'Name',
            sort: {
                direction: uiGridConstants.ASC,
                priority: 0
            },
            sortable: false,
            enableColumnMenu: false
        },
        {
            field: 'artwork',
            displayName: 'Artwork',
            sortable: false,
            enableColumnMenu: false,
            width: 70,
            cellTemplate: 'components/visual/grid/radio-button-template.html'
        },
        {
            field: 'audio',
            displayName: 'Audio',
            sortable: false,
            enableColumnMenu: false,
            width: 55,
            cellTemplate: 'components/visual/grid/radio-button-template.html'
        },
        {
            field: 'closedCaption',
            displayName: 'CC',
            sortable: false,
            enableColumnMenu: false,
            width: 40,
            cellTemplate: 'components/visual/grid/radio-button-template.html'
        },
        {
            field: 'image',
            displayName: 'Image',
            sortable: false,
            enableColumnMenu: false,
            width: 60,
            cellTemplate: 'components/visual/grid/radio-button-template.html'
        },
        {
            field: 'metadata',
            displayName: 'Metadata',
            sortable: false,
            enableColumnMenu: false,
            width: 80,
            cellTemplate: 'components/visual/grid/radio-button-template.html'
        },
        {
            field: 'subtitle',
            displayName: 'Subtitle',
            sortable: false,
            enableColumnMenu: false,
            width: 70,
            cellTemplate: 'components/visual/grid/radio-button-template.html'
        },
        {
            field: 'video',
            displayName: 'Video',
            sortable: false,
            enableColumnMenu: false,
            width: 60,
            cellTemplate: 'components/visual/grid/radio-button-template.html'
        },
        {
            field: 'defaultFlag',
            displayName: 'Default',
            sortable: false,
            enableColumnMenu: false,
            width: 70,
            cellTemplate: '<div class="checkbox-grid-cell" ng-if="!row.entity.custom" ng-click="grid.appScope.partnerPropCtrlr.handleDefaultCheckboxClick(row, $event)">' +
            '<input type="checkbox" ng-checked="COL_FIELD" /> <label> </label> </div>'
        }
    ];

    self.deliveryTemplatesGridOptions = {
        selectionRowHeaderColName: 'checkMarkRowHeaderCol',
        rowSelection: true,
        enableRowHeaderSelection: false,
        enableSelectAll: true,
        enableColumnResizing: false,
        enablePaginationControls: false,
        enableFullRowSelection: true,
        enableSorting: false,
        rowHeight: 26,
        multiSelect: true,
        minRowsToShow: 4,
        columnDefs: self.deliveryTemplateGridColumns,
        rowTemplate: '<div ' +
        'ng-repeat="(colRenderIndex, col) in colContainer.renderedColumns track by col.uid" ' +
        'ng-class="row.entity.hidden ? \'ui-grid-cell-hidden\' : \'\'" class="ui-grid-cell" ui-grid-cell> ' +
        '</div>',
        onRegisterApi: (gridApi) => {
            self.gridApi = gridApi;
            self.gridApi.selection.on.rowSelectionChanged($scope, self.gridRowSelectionChangedHandler);
        }
    };

    self.gridRowSelectionChangedHandler = (row, event) => {
        if (event) {
            if (row.isSelected) {
                if (!self.partnerObject.deliveryTemplates) {
                    self.partnerObject.deliveryTemplates = [];
                }
                self.partnerObject.deliveryTemplates.push({template: row.entity});
            } else {
                let templateIndex = 0;
                for (templateIndex; templateIndex < self.partnerObject.deliveryTemplates.length; templateIndex++) {
                    if (self.partnerObject.deliveryTemplates[templateIndex].template.id === row.entity.id) {
                        break;
                    }
                }
                self.partnerObject.deliveryTemplates.splice(templateIndex, 1);
            }
            self.editorForm.$setDirty();
        }
    };

    self.allAvailableLanguages = [];
    self.availableVideoRoles = [];
    self.intermediateTemplates = [];

    let setDefaultTemplate = (template, matchedTemplates) => {
        template.defaultFlag = (matchedTemplates && matchedTemplates.length === 1) ? matchedTemplates[0].defaultFlag : false;
    };

    let findTemplatesAddedToPartner = (matchedHandlerFunction) => {
        angular.forEach(PartnerEditService.deliveryTemplates, (template) => {
            let matchedTemplates = self.partnerObject.deliveryTemplates.filter((partnerTemplate) => {
                return partnerTemplate.template.id === template.id;
            });
            matchedHandlerFunction(template, matchedTemplates);
        });
    };

    self.handleDefaultCheckboxClick = (row, event) => {
        if (row.isSelected) {
            event.preventDefault();
            event.stopImmediatePropagation();
            angular.forEach(self.partnerObject.deliveryTemplates, (deliveryTemplate) => {
                if (deliveryTemplate.template.id === row.entity.id) {
                    deliveryTemplate.defaultFlag = !deliveryTemplate.defaultFlag;
                } else {
                    deliveryTemplate.defaultFlag = false;
                }
            });
            findTemplatesAddedToPartner(setDefaultTemplate);
            self.editorForm.$setDirty();
        }
    };

    let addRemainingDeliveryTemplatesToPartner = (template, matchedTemplates) => {
        if (!matchedTemplates.length) {
            self.partnerObject.deliveryTemplates.push({template: template});
        }
    };

    self.toggleRows = (evt) => {
        if (self.gridApi.selection.selectAll) {
            self.gridApi.selection.clearSelectedRows(evt);
            self.gridApi.selection.selectAll = false;
            self.partnerObject.deliveryTemplates = [];
        } else {
            self.gridApi.selection.selectAllVisibleRows(evt);
            self.gridApi.selection.selectAll = true;
            findTemplatesAddedToPartner(addRemainingDeliveryTemplatesToPartner);
        }
        self.editorForm.$setDirty();
    };

    let setSelectedGridTemplates = (template, matchedTemplates) => {
        $timeout(() => {
            if (matchedTemplates.length) {
                template.defaultFlag = matchedTemplates[0].defaultFlag;
                self.gridApi.selection.selectRow(template);
            } else {
                self.gridApi.selection.unSelectRow(template);
                template.defaultFlag = false;
            }
        });
    };

    self.setFormatsToDefault = () => {
        angular.forEach(self.DEFAULT_FORMAT_VALUES, (formatValue, formatKey) => {
            self.partnerObject[formatKey] = formatValue;
        });
        self.editorForm.$setDirty();
    };

    self.resetTabData = () => {
        self.partnerObject = PartnerEditService.partnerObject;
        findTemplatesAddedToPartner(setSelectedGridTemplates);
        resetRequiredArtworksGrid();
        updateServiceableLanguages();
    };

    //================//
    // Tab Management //
    //================//

    PartnerEditService.tabRevertFunction = self.resetTabData;
    PartnerEditService.tabSaveSuccessFunction = self.resetTabData;
    PartnerEditService.tabSaveErrorFunction = self.resetTabData;
    self.selectedParentPartner = undefined;
    self.availableParentPartners = [];
    self.initialArtworkAcceptProfiles = [];
    self.availableArtworkAcceptProfiles = [];
    self.availableCategoryTypes = [];
    self.availableServiceableLanguages = [];

    self.setTabData = () => {
        self.availableParentPartners.length = 0;

        let editDataPromise = PartnerEditService.getEditData().then((partnerData) => {
            self.partnerObject = PartnerEditService.partnerObject;
            if (!self.partnerObject.id) {
                self.setFormatsToDefault();
                self.partnerObject.artwork = [];
            }
            if (!_.toInteger(self.partnerObject.numberOfChildren)) {
                self.availableParentPartners = partnerData.partnersWithoutChildPartners.filter((partner) => {
                    return partner.id !== self.partnerObject.id;
                });
                if (self.partnerObject && self.partnerObject.parentId) {
                    let parentPartner = _.find(partnerData.partnersWithoutChildPartners, ['id', self.partnerObject.parentId]);
                    if (parentPartner) {
                        self.selectedParentPartner = _.cloneDeep(parentPartner);
                    }
                }
            }

            // if on the edit view, get the children
            if (_.get(self, 'partnerObject.id') && _.toNumber(_.get(self, 'partnerObject.numberOfChildren'))) {
                PartnerResourceFactory.getChildren({id: self.partnerObject.id}).$promise.then((childPartners) => {
                    self.childPartners = childPartners;
                });
            }

            self.deliveryTemplatesGridOptions.data = PartnerEditService.deliveryTemplates;
            if (self.partnerObject.deliveryTemplates && self.partnerObject.deliveryTemplates.length) {
                findTemplatesAddedToPartner(setSelectedGridTemplates);
            }

            // get the available delivery endpoints
            PartnerEditService.getDeliveryEndpointsForPartner($routeParams.id).then((deliveryEndpoints) => {
                self.availableDeliveryEndpoints = deliveryEndpoints;
            });

            // get the available email endpoints
            PartnerEditService.getEmailEndpointsForPartner($routeParams.id).then((emailEndpoints) => {
                self.availableEmailEndpoints = emailEndpoints;
            });
        });

        // for video role requirement grid
        let rolesPromise = RoleResourceFactory.query({mediaType: 'Video'})
            .$promise.then((availableVideoRoles) => {
            self.availableVideoRoles = availableVideoRoles.filter((role) => {
                // filter out "Main Video"
                return !role.isDefault;
            });
        });

        // for default template selector
        let intermediateTemplatesPromise = IntermediateTemplatesResourceFactory.query()
            .$promise.then((intermediateTemplates) => {
            self.intermediateTemplates = intermediateTemplates;
        });

        // for required MAPs grid (artwork accept profiles)
        let artworkAcceptProfilesPromise = AcceptProfileResourceFactory.query({profileType: 'OTHER'}).$promise.then((acceptProfiles) => {
            self.availableArtworkAcceptProfiles = acceptProfiles;
        });
        let categoryTypesPromise = PackageResourceFactory.getCategories({view: 'EXTENDED'}).$promise.then((availableCategories) => {
            self.availableCategoryTypes = availableCategories;
            self.availableCategoryTypes.push({name: 'Both'});
        });

        // get the available territories
        let territoriesPromise = TerritoriesService.getAllTerritories({fetchLimit: 0, includeHidden: false}).then((territories) => {
            // Place USA at the top of the list
            const territoriesWithUsaFirst = _.chain(territories)
                .partition({name: 'USA'})
                .flatten()
                .value();
            _.set(self, 'availableTerritories', territoriesWithUsaFirst);
        });

        let languagesPromise = LanguagesService.getAllLanguages().then((allAvailableLanguages) => {
            self.allAvailableLanguages = _
                .chain(allAvailableLanguages)
                .orderBy('name')
                .partition({name: 'English'})
                .flatten()
                .value();
        });

        let dynamicPropertiesPromise = DynamicPropertiesService.queryForProperties().then(() => {
            self.availablePartnerTypes = DynamicPropertiesService.getPropValueAsList('partner.types');
        });

        return $q.all([
            editDataPromise,
            rolesPromise,
            intermediateTemplatesPromise,
            artworkAcceptProfilesPromise,
            categoryTypesPromise,
            territoriesPromise,
            languagesPromise,
            dynamicPropertiesPromise
        ]).then(() => {
            resetRequiredArtworksGrid();
            updateServiceableLanguages();
        });
    };

    //======================//
    // Other Template Logic //
    //======================//

    self.enabledVideoRolesUpdated = (context, newEnabledEntities) => {
        self.partnerObject.videoRoles = newEnabledEntities;
        self.editorForm.$setDirty();
    };

    $scope.$watch(() => {
        return self.editorForm;
    }, () => {
        if (self.editorForm) {
            PartnerEditService.editorForm = self.editorForm;
        }
    });

    self.setPartnerObjectParentPartnerId = () => {
        if (self.partnerObject && self.selectedParentPartner) {
            self.partnerObject.parentId = self.selectedParentPartner.id;
        } else if (self.partnerObject) {
            self.partnerObject.parentId = undefined;
        }
    };

    self.navigateToChildPartner = (childPartnerId) => {
        $location.url(`/system/partners/edit/${childPartnerId}/properties`);
    };

    self.setTabData();

}).filter('stringToNumber', () => {
    return (numberAsString) => {
        const BASE_TEN_RADIX = 10;
        return parseInt(numberAsString, BASE_TEN_RADIX);
    };
});
